import React from 'react';
import PropTypes from 'prop-types';
import { tokens, useBrsCreateStyles } from '../BrsCreateStyles';
import {
  DAYS,
  computeWeekDates,
  displayPct,
  hasSplitWeekData,
  normalizeSchedule,
  pctClass,
} from './splitWeekUtils';

/**
 * Read-only renderer for a saved split-week rotation schedule on a
 * `broadcastrequest` record. Gracefully renders a "uniform rotation" notice
 * when the record has no schedule or all days are 100% (equivalent to uniform).
 *
 * Tolerates missing/null fields — designed to round-trip data from the backend
 * during the Phase 3/4 go-live window without runtime errors.
 */
export const SplitWeekDisplay = ({ record }) => {
  const classes = useBrsCreateStyles();

  if (!record) return null;

  if (!hasSplitWeekData(record.split_week_schedule)) {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: tokens.bgDark,
          color: tokens.textDim,
          fontSize: 12,
          borderLeft: `3px solid ${tokens.border}`,
          margin: 8,
        }}>
        <strong style={{ color: tokens.text }}>Uniform rotation</strong>
        {' — '}
        this spot does not use split-week scheduling. The traffic instruction PDF renders in the
        standard format.
      </div>
    );
  }

  const schedule = normalizeSchedule(record.split_week_schedule);
  const weekDates = computeWeekDates(record.start_date);
  const total = DAYS.reduce((acc, d) => acc + schedule[d.key], 0);

  return (
    <div style={{ padding: 12, background: tokens.bgDark, margin: 8 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: tokens.textDim,
          marginBottom: 8,
          fontWeight: 600,
        }}>
        Split Week Schedule
      </div>
      <table className={classes.timelineGrid}>
        <thead>
          <tr>
            <th>Spot</th>
            {DAYS.map((d, i) => (
              <th key={d.key}>
                {d.label}
                {weekDates[i] && (
                  <>
                    <br />
                    <span style={{ fontSize: 10, fontWeight: 400 }}>{weekDates[i]}</span>
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span className={classes.isciMono}>{record.isciasset?.isci || 'No ISCI'}</span>
              <span className={classes.spotName}>{record.title || 'Untitled'}</span>
            </td>
            {DAYS.map((d) => {
              const value = schedule[d.key];
              return (
                <td key={d.key}>
                  <div
                    className={`${classes.dayCell} ${pctClass(classes, value)}`}
                    style={{ cursor: 'default' }}>
                    {displayPct(value)}
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: tokens.textDim,
          display: 'flex',
          gap: 6,
        }}>
        <span>Spot total across the week:</span>
        <strong style={{ color: tokens.accent }}>{total}%</strong>
      </div>
    </div>
  );
};

SplitWeekDisplay.propTypes = {
  // `record` comes from react-admin's Datagrid expand mechanism. It may be
  // undefined during the initial render pass.
  record: PropTypes.shape({
    title: PropTypes.string,
    start_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isciasset: PropTypes.shape({
      isci: PropTypes.string,
    }),
    split_week_schedule: PropTypes.object,
  }),
};

SplitWeekDisplay.defaultProps = {
  record: null,
};
